import { objectToFormData } from '@vindr/utils/converter';
import { AxiosError, AxiosResponse } from 'axios';
import api from '../api';
import { SORT_ORDER } from '../constants/filter.constant';
import { DefaultResponse } from '../interfaces/index';

const ESIGN_URL = '/esign/v1/esigns';

export enum ESIGN_STATUS {
  'Unsigned' = 0,
  'New' = 1,
  'ESIGN.Waiting' = 2,
  'Approver Signed' = 3, // show pdf
  'Approver Failed' = 4, // possible show pdf
  'Revoking' = 5,
  'Revoke Failed' = 6, // show pdf
  'Revoked' = 7,
  'Submitter Signed' = 8, // show pdf
  'Submitter Failed' = 9,
}

export interface EsignHistoryParams {
  accession_no?: string;
  created_at?: string;
  created_at_from?: string;
  created_at_to?: string;
  executed_at_from?: string;
  executed_at_to?: string;
  institution_ids?: string[];
  limit?: number;
  offset?: number;
  order_id?: string;
  patient_id?: string;
  patient_name?: string;
  processing_id?: string;
  scheduled_date_from?: string;
  scheduled_date_to?: string;
  sort_field?: 'created_at' | 'executed_at' | 'scheduled_date' | '';
  sort_order?: SORT_ORDER;
  status?: string;
}

export interface EsignHistory {
  accession_no: string;
  created_at: string;
  executed_at: string;
  institution: string;
  institution_id: string;
  order_id: string;
  patient_id: string;
  patient_name: string;
  procedure_name: string;
  processing_id: string;
  reason_of_failure: null | string;
  receiver: string;
  report_id: string;
  revoke_id?: string;
  scheduled_date: string;
  sender: string;
  status: ESIGN_STATUS;
}

export type EsignHistoryResponse = DefaultResponse<EsignHistory[]>;

export const getEsignHistory = async (params: EsignHistoryParams) => {
  const response = (await api({
    url: ESIGN_URL + '/latest-for-each-order',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<EsignHistoryResponse>;

  return response.data;
};

export interface EsignHistoryDetail {
  accession_no: string;
  created_at: string;
  executed_at: string;
  institution: string;
  institution_id: string;
  order_id: string;
  patient_id: string;
  patient_name: string;
  procedure_name: string;
  processing_id: string;
  reason_of_failure: null;
  receiver: string;
  report_id: string;
  scheduled_date: string;
  sender: string;
  signed_report: string;
  status: ESIGN_STATUS;
  unsigned_report: string;
}

export type EsignHistoryDetailResponse = DefaultResponse<EsignHistoryDetail>;

export const getEsignHistoryDetail = async (
  id: EsignHistory['processing_id']
) => {
  const response = (await api({
    url: ESIGN_URL + `/${id}`,
    method: 'GET',
  })) as AxiosResponse<EsignHistoryDetailResponse>;

  return response.data;
};

export interface CreateEsignParams {
  report_id: string;
  content: string;
  credential: string;
  report_version_id: string;
}

export interface CreateSignParamResponse extends DefaultResponse {
  data: { esign_id: string };
  message: string;
  request_id: string;
}

export const createEsign = async (data: CreateEsignParams) => {
  const formData = objectToFormData(data);

  const response = (await api({
    url: ESIGN_URL,
    method: 'POST',
    data: formData,
  })) as AxiosResponse<CreateSignParamResponse>;

  return response.data;
};

export interface RevokeEsignParams {
  credential: string;
  ids: string[];
}

export const revokeEsign = async (data: RevokeEsignParams) => {
  const response = (await api({
    url: ESIGN_URL + `/revoke`,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ResignEsignParams {
  ids: string[];
}

export const resendEsign = async (data: ResignEsignParams) => {
  const response = (await api({
    url: ESIGN_URL + `/resend`,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface DeleteEsignParams {
  ids: string[];
}

export const deleteEsign = async (data: DeleteEsignParams) => {
  const response = (await api({
    url: ESIGN_URL + `/delete`,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface GetFullEsignHistoryByOrderParams {
  limit: number;
  offset: number;
  order_id: string;
}

export interface FullEsignHistoryByOrder {
  processing_id: string;
  created_at: string;
  executed_at: string;
  reason_of_failure: null | string;
  status: ESIGN_STATUS;
}

export type FullEsignHistoryByOrderResponse = DefaultResponse<
  FullEsignHistoryByOrder[]
> & {
  request_id: string;
};

export type FullEsignHistoryByOrderErrorResponse = {
  request_id: string;
  code: string;
  message: string;
  server_time: number;
  data: Record<string, any>;
};

export const getFullEsignHistoryByOrder = async (
  params: GetFullEsignHistoryByOrderParams
) => {
  try {
    const response = (await api({
      url: ESIGN_URL + '/all-by-order',
      method: 'GET',
      params,
      timeout: 30_000,
    })) as AxiosResponse<FullEsignHistoryByOrderResponse>;

    return response.data;
  } catch (error) {
    throw error as AxiosError<FullEsignHistoryByOrderErrorResponse>;
  }
};
